import React, { Component, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import _ from '@/utils/lodash';
import { getPagePath } from '@/utils/pagePaths';
import { trackContactClick, trackExternalLink } from '@/services/analytics';
import { frameEditable, inlineEditingEnabled } from '@components/preview/editGates';
import { useAppContext } from '@/context/AppContext';
import { isPageHref, prefixInternalHrefs, stripBasename } from '@/subsites/links';
import { contentWidthCss } from '@/blocks/content-width';
// html-content.css is loaded via index.html to prevent FOUC during SSR

const InlinePageEditor = lazy(() => import('@components/preview/InlinePageEditor'));

/**
 * Html Content Component
 * Renders HTML content from CMS
 * Uses semantic HTML: article
 */
class Html extends Component {
    constructor() {
        super(...arguments);
        this.active = true;
        this.state = { editing: false };
    }

    /**
     * Replace ##pageId## placeholders in href attributes with actual URLs
     * @param {string} html - HTML content with ##pageId## placeholders
     * @param {Object} pages - All pages object (pages[id] = page)
     * @param {string} [basename] - The subsite prefix, e.g. /~spring-2026
     * @returns {string} HTML with replaced URLs
     */
    replacePageIdPlaceholders = (html, pages, basename = '') => {
        if (!html || !pages) return html;
        
        // Match href attributes containing ##pageId## format
        // Handles: href="##pageId##", href='##pageId##', and variations with spaces
        return html.replace(/href\s*=\s*(["'])##([^#]+)##\1/gi, (match, quote, pageId) => {
            const page = pages[pageId];
            if (page) {
                const path = getPagePath(page, pages);
                const url = `${basename}${path ? `/${path}` : ''}` || '/';
                return `href=${quote}${url}${quote}`;
            }
            // If page not found, use #
            return `href=${quote}#${quote}`;
        });
    };

    /**
     * Normalize URL to have leading slash
     * @param {string} url - URL string
     * @returns {string} URL with leading slash
     */
    normalizeUrl = (url) => {
        if (!url || typeof url !== 'string') return '';
        return url.startsWith('http') || url.startsWith('/') ? url : '/' + url;
    };

    /**
     * Check if file has new srcset structure
     * @param {Object} file - File object from database
     * @returns {boolean} True if file has srcset structure
     */
    hasNewSrcsetStructure = (file) => {
        return file?.url?.srcset && Object.keys(file.url.srcset).length > 0;
    };

    /**
     * Generate srcset paths from original path
     * @param {string} original - Original file path (e.g., images/2024/photo-abc-original.jpg)
     * @returns {Object|null} srcset object with width keys, or null if pattern doesn't match
     */
    generateSrcsetFromOriginal = (original) => {
        if (!original) return null;
        // images/2024/photo-abc-original.jpg → images/2024/photo-abc
        const basePath = original.replace(/-original\.[^.]+$/, '');
        if (basePath === original) return null; // Pattern didn't match
        return {
            480: this.normalizeUrl(`${basePath}-480.webp`),
            768: this.normalizeUrl(`${basePath}-768.webp`),
            1280: this.normalizeUrl(`${basePath}-1280.webp`),
            1920: this.normalizeUrl(`${basePath}-1920.webp`),
        };
    };

    /**
     * Get available sizes based on original image dimensions
     * Sizes larger than the original width are not generated (no upscaling)
     * @param {number|null} originalWidth - Original image width in pixels
     * @returns {number[]} Array of available widths
     */
    getAvailableSizes = (originalWidth) => {
        const ALL_SIZES = [3000, 1920, 1280, 768, 480];
        if (!originalWidth) return ALL_SIZES; // If unknown, assume all available
        return ALL_SIZES.filter(size => size <= originalWidth);
    };

    /**
     * Get srcset data from file object
     * Supports both new srcset structure and legacy URL structure
     * Filters out sizes larger than the original image (which weren't generated)
     * @param {Object} file - File object from database
     * @returns {{ srcset: Object, defaultSrc: string, originalWidth: number|null }} Object with srcset map, default src, and original width
     */
    getSrcsetData = (file) => {
        if (!file || !file.url) return { srcset: {}, defaultSrc: '', originalWidth: null };
        
        const originalWidth = file?.dimensions?.width || null;
        const availableSizes = this.getAvailableSizes(originalWidth);
        
        // Helper to filter srcset by available sizes
        const filterSrcset = (srcset) => {
            const filtered = {};
            Object.entries(srcset).forEach(([width, url]) => {
                const w = parseInt(width, 10);
                if (availableSizes.includes(w) && url) {
                    filtered[width] = url;
                }
            });
            return filtered;
        };
        
        // Helper to get default src from srcset (best available)
        const getDefault = (srcset) => {
            // Try sizes in order of preference, respecting available sizes
            for (const size of [1280, 1920, 768, 480]) {
                if (srcset[size]) return srcset[size];
            }
            return this.normalizeUrl(file.url?.original) || '';
        };
        
        // If url is a simple string, return it directly
        if (typeof file.url === 'string') {
            return { 
                srcset: {},
                defaultSrc: this.normalizeUrl(file.url),
                originalWidth
            };
        }
        
        // New structure: use srcset directly
        if (this.hasNewSrcsetStructure(file)) {
            const rawSrcset = {};
            Object.entries(file.url.srcset).forEach(([width, url]) => {
                rawSrcset[width] = this.normalizeUrl(url);
            });
            const srcset = filterSrcset(rawSrcset);
            return { srcset, defaultSrc: getDefault(srcset), originalWidth };
        }
        
        // Try to derive WebP paths from original filename
        // This works for images that have been regenerated but DB not updated
        const derivedSrcset = this.generateSrcsetFromOriginal(file.url?.original);
        if (derivedSrcset) {
            const srcset = filterSrcset(derivedSrcset);
            return { srcset, defaultSrc: getDefault(srcset), originalWidth };
        }
        
        // Legacy structure: map old sizes to new widths
        // Use WebP versions if available
        const rawSrcset = {};
        
        if (file.webp?.sm || file.url?.sm) {
            rawSrcset[480] = this.normalizeUrl(file.webp?.sm || file.url?.sm);
        }
        if (file.webp?.md || file.url?.md) {
            rawSrcset[768] = this.normalizeUrl(file.webp?.md || file.url?.md);
        }
        if (file.webp?.lg || file.url?.lg) {
            rawSrcset[1280] = this.normalizeUrl(file.webp?.lg || file.url?.lg);
            rawSrcset[1920] = this.normalizeUrl(file.webp?.lg || file.url?.lg);
        }
        
        const srcset = filterSrcset(rawSrcset);
        return { srcset, defaultSrc: getDefault(srcset), originalWidth };
    };

    /**
     * Build srcset attribute string from srcset object
     * @param {Object} srcset - Object with width keys and URL values
     * @returns {string} srcset attribute value
     */
    buildSrcsetString = (srcset) => {
        if (!srcset || Object.keys(srcset).length === 0) return '';
        
        return Object.entries(srcset)
            .filter(([, url]) => url)
            .map(([width, url]) => `${url} ${width}w`)
            .join(', ');
    };

    /**
     * Get sizes attribute based on style width
     * @param {string} styleWidth - Width from style attribute (e.g., "50%", "300px", "20em")
     * @returns {string} sizes attribute value
     */
    getSizesAttribute = (styleWidth) => {
        if (!styleWidth) {
            // Default for content column (60-75vw)
            return '(max-width: 768px) 100vw, 70vw';
        }
        
        // If width is a percentage, use that for sizes
        if (styleWidth.endsWith('%')) {
            const percent = parseInt(styleWidth, 10);
            return `(max-width: 768px) 100vw, ${percent}vw`;
        }
        
        // If width is in em, convert roughly to viewport
        if (styleWidth.endsWith('em')) {
            const em = parseFloat(styleWidth);
            // Rough conversion: 1em ≈ 16px, content column ≈ 65ch ≈ 1040px
            const percent = Math.round((em * 16 / 1040) * 100);
            return `(max-width: 768px) 100vw, ${Math.min(percent, 100)}vw`;
        }
        
        // If width is in px, use media query approach
        if (styleWidth.endsWith('px')) {
            const px = parseInt(styleWidth, 10);
            return `(max-width: ${px}px) 100vw, ${px}px`;
        }
        
        // Default
        return '(max-width: 768px) 100vw, 70vw';
    };

    /**
     * Get the optimal size key based on target width (for legacy fallback)
     * @param {number|null} targetWidth - Target display width in pixels
     * @returns {string} Size key: 'sm', 'md', or 'lg'
     */
    getOptimalSizeKey = (targetWidth) => {
        if (targetWidth === null || targetWidth <= 0) {
            return 'md'; // Default to medium
        }
        
        // Account for retina displays (2x pixel density)
        const retinaWidth = targetWidth * 2;
        
        // Size thresholds (should match image processing config)
        if (retinaWidth <= 400) return 'sm';
        if (retinaWidth <= 800) return 'md';
        return 'lg';
    };

    /**
     * Get optimized image URLs from a file object based on target display width
     * Returns both WebP and original format URLs for use in picture element
     * @param {Object} file - File object from database
     * @param {number|null} targetWidth - Target display width in pixels (null for default behavior)
     * @returns {{ webp: string|null, fallback: string }} Object with webp and fallback URLs
     * @deprecated Use getSrcsetData for responsive srcset support
     */
    getOptimizedImageUrls = (file, targetWidth = null) => {
        if (!file || !file.url) return { webp: null, fallback: '' };
        
        // If url is a simple string, return it directly (no WebP available)
        if (typeof file.url === 'string') {
            return { 
                webp: null, 
                fallback: this.normalizeUrl(file.url) 
            };
        }
        
        const sizeKey = this.getOptimalSizeKey(targetWidth);
        
        // Get fallback URL (original format) - try selected size, then fallbacks
        let fallbackUrl = file.url?.[sizeKey] || file.url?.lg || file.url?.md || file.url?.sm || file.url?.original;
        
        // Get WebP URL if available
        let webpUrl = file.webp?.[sizeKey] || file.webp?.lg || file.webp?.md || file.webp?.sm;
        
        return {
            webp: webpUrl ? this.normalizeUrl(webpUrl) : null,
            fallback: this.normalizeUrl(fallbackUrl),
        };
    };

    /**
     * Get the optimized image URL from a file object based on target display width
     * Chooses the smallest size that's larger than the target width for best quality/performance
     * @param {Object} file - File object from database
     * @param {number|null} targetWidth - Target display width in pixels (null for default behavior)
     * @returns {string} Optimized image URL with leading slash
     * @deprecated Use getSrcsetData for responsive srcset support
     */
    getOptimizedImageUrl = (file, targetWidth = null) => {
        const { fallback } = this.getOptimizedImageUrls(file, targetWidth);
        return fallback;
    };

    /**
     * Extract width value from style attribute string
     * @param {string} styleAttr - Style attribute content
     * @returns {number|null} Width in pixels or null if not found
     */
    extractWidthFromStyle = (styleAttr) => {
        if (!styleAttr) return null;
        
        // Match width: Npx pattern
        const match = styleAttr.match(/width:\s*(\d+)px/i);
        if (match) {
            return parseInt(match[1], 10);
        }
        
        return null;
    };

    /**
     * Extract width from HTML attributes (width attribute or style attribute)
     * @param {string} beforeSrc - HTML attributes before src
     * @param {string} afterSrc - HTML attributes after src
     * @returns {number|null} Width in pixels or null if not found
     */
    extractImageWidth = (beforeSrc, afterSrc) => {
        const fullAttrs = beforeSrc + afterSrc;
        
        // First check for width attribute
        const widthAttrMatch = fullAttrs.match(/\swidth\s*=\s*["']?(\d+)["']?/i);
        if (widthAttrMatch) {
            return parseInt(widthAttrMatch[1], 10);
        }
        
        // Then check style attribute for width
        const styleMatch = fullAttrs.match(/\sstyle\s*=\s*["']([^"']*)["']/i);
        if (styleMatch) {
            return this.extractWidthFromStyle(styleMatch[1]);
        }
        
        return null;
    };

    /**
     * Extract style width value from HTML attributes
     * @param {string} beforeSrc - HTML attributes before src
     * @param {string} afterSrc - HTML attributes after src
     * @returns {string|null} Width value (e.g., "50%", "300px", "20em") or null
     */
    extractStyleWidth = (beforeSrc, afterSrc) => {
        const fullAttrs = beforeSrc + afterSrc;
        const styleMatch = fullAttrs.match(/\sstyle\s*=\s*["']([^"']*)["']/i);
        if (styleMatch) {
            const widthMatch = styleMatch[1].match(/width:\s*([^;]+)/i);
            if (widthMatch) {
                return widthMatch[1].trim();
            }
        }
        return null;
    };

    /**
     * Replace ##imageId## placeholders in src attributes with optimized image URLs
     * Uses srcset and sizes attributes for responsive images
     * @param {string} html - HTML content with ##imageId## placeholders
     * @param {Object} files - All files object (files[id] = file)
     * @returns {string} HTML with replaced image sources and responsive attributes
     */
    replaceImageIdPlaceholders = (html, files) => {
        if (!html || !files) return html;
        
        // Match img tags containing src="##imageId##" or src='##imageId##'
        // Captures the entire img tag to allow enhancement
        return html.replace(/<img([^>]*)\ssrc\s*=\s*(["'])##([^#]+)##\2([^>]*)>/gi, (match, beforeSrc, quote, imageId, afterSrc) => {
            const file = files[imageId];
            if (file) {
                // Get srcset data
                const { srcset, defaultSrc } = this.getSrcsetData(file);
                const srcsetStr = this.buildSrcsetString(srcset);
                
                // Extract style width for sizes calculation
                const styleWidth = this.extractStyleWidth(beforeSrc, afterSrc);
                const sizesAttr = this.getSizesAttribute(styleWidth);
                
                // Build alt text
                const hasAlt = /\salt\s*=/i.test(beforeSrc + afterSrc);
                let altAttr = '';
                if (!hasAlt && file.description) {
                    altAttr = ` alt="${file.description.replace(/"/g, '&quot;')}"`;
                }
                
                // Build dimension attributes (for aspect ratio placeholder)
                const hasWidth = /\swidth\s*=/i.test(beforeSrc + afterSrc);
                const hasHeight = /\sheight\s*=/i.test(beforeSrc + afterSrc);
                const hasStyleWidth = /style\s*=\s*["'][^"']*width:/i.test(beforeSrc + afterSrc);
                
                let dimensionAttrs = '';
                if (file.dimensions && !hasWidth && !hasHeight && !hasStyleWidth) {
                    dimensionAttrs = ` width="${file.dimensions.width}" height="${file.dimensions.height}"`;
                }
                
                // Build loading and decoding attributes
                const hasLoading = /\sloading\s*=/i.test(beforeSrc + afterSrc);
                let loadingAttr = '';
                if (!hasLoading) {
                    loadingAttr = ' loading="lazy"';
                }
                
                const hasDecoding = /\sdecoding\s*=/i.test(beforeSrc + afterSrc);
                let decodingAttr = '';
                if (!hasDecoding) {
                    decodingAttr = ' decoding="async"';
                }
                
                // Build srcset and sizes attributes
                let responsiveAttrs = '';
                if (srcsetStr) {
                    responsiveAttrs = ` srcset="${srcsetStr}" sizes="${sizesAttr}"`;
                }
                
                // Build the img tag with responsive attributes
                const imgTag = `<img${beforeSrc} src=${quote}${defaultSrc}${quote}${responsiveAttrs}${altAttr}${dimensionAttrs}${loadingAttr}${decodingAttr}${afterSrc}>`;
                return imgTag;
            }
            // If image not found, return empty img or placeholder
            return `<img${beforeSrc} src="" alt="Image not found"${afterSrc}>`;
        });
    };

    render() {
        const { content = '', pages = {}, files = {}, contentMaxWidth, gallery, editPath, editHtml, basename, fill } = this.props;
        // Remove scripts first
        let processedHtml = content.replace(/<script[^<]*<\/script>/gi, '');
        // Replace ##imageId## placeholders with optimized image URLs
        processedHtml = this.replaceImageIdPlaceholders(processedHtml, files);
        // Replace ##pageId## placeholders with actual URLs
        processedHtml = this.replacePageIdPlaceholders(processedHtml, pages, basename);
        // Typed /page links stay inside a subsite too
        processedHtml = prefixInternalHrefs(processedHtml, basename);
        
        // For background (fullscreen) mode, content should span 100% width with no padding
        // For other modes, apply custom max-width (stored as ch value), default to 65ch
        // A column of a columns block fills its column too; the block sets the width.
        const bare = gallery === 'background' || fill;
        const style = bare
            ? {} // No max-width constraint for fullscreen background
            : { maxWidth: contentWidthCss(contentMaxWidth) };
        
        // Remove px-4 padding for background mode
        const className = bare
            ? 'html-page mx-auto w-full'
            : 'html-page mx-auto px-4 w-full';

        // Block text passes `editing`, and becomes editable only while its block is
        // open. Text outside the block stack keeps its always-editable preview.
        const editable = this.state.editing && (this.props.editing === undefined || this.props.editing);
        if (editable) {
            return (
                <Suspense fallback={
                    <article
                        className={className}
                        style={style}
                        data-page-path="page.content"
                        dangerouslySetInnerHTML={{ __html: processedHtml }}
                    />
                }>
                    <InlinePageEditor
                        html={editHtml != null ? editHtml : content}
                        processedHtml={processedHtml}
                        pages={pages}
                        files={files}
                        editPath={editPath || 'content'}
                        contentMaxWidth={contentMaxWidth}
                        gallery={gallery}
                        className={className}
                        style={style}
                    />
                </Suspense>
            );
        }
        
        return (
            <article
                ref={this.articleRef}
                className={className}
                style={style}
                data-page-path="page.content"
                dangerouslySetInnerHTML={{ __html: processedHtml }}
            />
        );
    }

    articleRef = (ref) => {
        // Remove listener from old element if it exists
        if (this.articleElement) {
            this.articleElement.removeEventListener('click', this.captureLinkClick);
        }
        
        // Store reference and attach listener to new element
        this.articleElement = ref;
        if (ref) {
            ref.addEventListener('click', this.captureLinkClick);
        }
    };

    captureLinkClick = (e) => {
        // Fullscreen click-through sits behind this text. In the editor, a link
        // in the overlay must not navigate — the click is for selecting or editing.
        if (
            window.parent !== window
            && inlineEditingEnabled()
            && this.articleElement?.closest?.('[data-page-path="page.advance"]')
        ) {
            const anchor = e.target?.closest?.('a');
            if (anchor && this.articleElement.contains(anchor)) {
                e.preventDefault();
                e.stopPropagation();
            }
            return;
        }

        // Find the closest anchor element
        let target = e.target;
        while (target && target.tagName !== 'A') {
            target = target.parentElement;
        }
        
        if (target && target.tagName === 'A') {
            const href = target.getAttribute('href');
            if (!href || href === '#') return;
            
            // Check if it's an internal link
            // Internal links are: relative paths (starting with /) or same host
            const isRelative = href.startsWith('/');
            const isSameHost = target.hostname && target.hostname === window.location.hostname;
            const isMailto = /^mailto:/i.test(href);
            const isTel = /^tel:/i.test(href);
            const isProtocolLink = isMailto || isTel || /^javascript:/i.test(href);
            
            // Track contact links (mailto, tel)
            if (isMailto) {
                const email = href.replace(/^mailto:/i, '').split('?')[0];
                trackContactClick('email', email);
            } else if (isTel) {
                const phone = href.replace(/^tel:/i, '');
                trackContactClick('phone', phone);
            }
            
            // Track external links (not internal, not protocol links)
            const isExternal = !isRelative && !isSameHost && !isProtocolLink;
            if (isExternal) {
                const linkText = target.textContent?.trim() || target.getAttribute('title') || '';
                trackExternalLink(href, linkText, this.props.name);
            }
            
            // Only handle internal links (relative or same host), skip protocol links and external links
            if ((isRelative || isSameHost) && !isProtocolLink) {
                // Get the path - use pathname if available (for full URLs), otherwise use href (for relative paths)
                let path = target.pathname || href;
                
                // Ensure path starts with /
                if (!path.startsWith('/')) {
                    path = `/${path}`;
                }

                // The router adds the subsite prefix back. Files and other
                // subsites are not its pages, so the browser loads those.
                const { basename } = this.props;
                const inner = stripBasename(path, basename);
                if (basename && inner === path && !isPageHref(path)) return;

                e.preventDefault();
                e.stopPropagation();
                
                if (this.props.history) {
                    this.props.history.push(inner);
                }
            }
        }
    };

    componentDidMount() {
        this.syncPageEditing = () => {
            const onPage = document.documentElement.dataset.editGroup === 'page';
            const editing = frameEditable() && onPage;
            this.setState((current) => (current.editing === editing ? null : { editing }));
        };
        document.addEventListener('portfolio:edit-group', this.syncPageEditing);
        this.syncPageEditing();
        const content = this.props.content || '';
        const node = document.createElement('div');
        node.innerHTML = content;
        const scripts = [...node.getElementsByTagName('script')];
        const loadedScripts = [...document.getElementsByTagName('script')].map(s => s.src);
        const scriptsSrc = scripts.filter(script => !!script.src).map(s => s.src).filter(src => !_.find(loadedScripts, src));
        const scriptsToRun = scripts.map(script => script.innerText).filter(x => !!x.replace(/\s/gi, ''));

        Promise.all(scriptsSrc.map(loadScript))
            .then(() =>
                this.active && scriptsToRun.forEach(script => {
                    try {
                        window.eval(script);
                    } catch (e) {
                        console.error(e);
                    }
                })
            );
    }

    componentWillUnmount() {
        this.active = false;
        document.removeEventListener('portfolio:edit-group', this.syncPageEditing);
        // Remove event listener on unmount
        if (this.articleElement) {
            this.articleElement.removeEventListener('click', this.captureLinkClick);
        }
    }
}

Html.propTypes = {
    content: PropTypes.string,
    editHtml: PropTypes.string,
    editPath: PropTypes.string,
    /** Set by blocks: the text is editable only while this is true */
    editing: PropTypes.bool,
    contentMaxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    gallery: PropTypes.string,
    /** Fill the parent with no width limit or side padding (a column of a columns block). */
    fill: PropTypes.bool,
    history: PropTypes.object,
    pages: PropTypes.object,
    files: PropTypes.object,
    /** The subsite prefix (/~slug) when the page is served inside one */
    basename: PropTypes.string,
};

const loadScript = (src) => new Promise((res, rej) => {
    const script = document.createElement('script');
    script.onload = res;
    script.src = src;
    document.head.appendChild(script);
});

function HtmlWithNavigate(props) {
    const navigate = useNavigate();
    const { content } = useAppContext();
    const history = props.history ?? {
        push: (path) => navigate(path),
        replace: (path) => navigate(path, { replace: true }),
    };
    const basename = props.basename ?? content?.subsite?.basename;
    return <Html {...props} history={history} basename={basename} />;
}

export default HtmlWithNavigate;
export { Html };
